import {
  FunctionComponent,
  SVGProps,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useTranslations } from '@npco/common-utils-translations'

import { ReactComponent as InvoicingSendBySms } from 'assets/svg/authenticate-slide.svg'
import { ReactComponent as InvoicingCustomisationSvg } from 'assets/svg/invoices-welcome.svg'
import { ReactComponent as InvoicingScheduleSvg } from 'assets/svg/time.svg'
import { useIsInvoicingEnabled } from 'hooks/invoices/useIsInvoicingEnabled'
import { ProductTourStatus } from 'hooks/useCustomerQuery/graphql/getCustomer.types'

import { useCustomerProductTourStatus } from '../../../../../hooks/useCustomerProductTourStatus'
import { translations } from './useInvoicingWelcomeModal.i18n'

type InvoicingWelcomeSlide = {
  Svg: FunctionComponent<SVGProps<SVGSVGElement>>
  description: string
  heading: string
  key: keyof ProductTourStatus
}

export const useInvoicingWelcomeModal = () => {
  const [slideIndex, setSlideIndex] = useState(0)

  const isInvoicingEnabled = useIsInvoicingEnabled()

  const t = useTranslations(translations)

  const {
    showInvoicesCustomisationWelcome,
    showInvoicesScheduleSendWelcome,
    showInvoicesSendBySmsWelcome,
    updateProductTourStatus,
  } = useCustomerProductTourStatus()

  const slides = useMemo(() => {
    const nextSlides: InvoicingWelcomeSlide[] = []

    if (isInvoicingEnabled) {
      if (showInvoicesSendBySmsWelcome) {
        nextSlides.push({
          Svg: InvoicingSendBySms,
          description: t('sendViaSmsDescription'),
          heading: t('sendViaSmsHeading'),
          key: 'showInvoicesSendBySmsWelcome',
        })
      }

      if (showInvoicesScheduleSendWelcome) {
        nextSlides.push({
          Svg: InvoicingScheduleSvg,
          description: t('scheduleSendDescription'),
          heading: t('scheduleSendHeading'),
          key: 'showInvoicesScheduleSendWelcome',
        })
      }

      if (showInvoicesCustomisationWelcome) {
        nextSlides.push({
          Svg: InvoicingCustomisationSvg,
          description: t('customisationDescription'),
          heading: t('customisationHeading'),
          key: 'showInvoicesCustomisationWelcome',
        })
      }
    }

    return nextSlides
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const markSlideAsSeen = useCallback(
    async (key: keyof ProductTourStatus) => {
      // NOTE: this will be skipped if the value is already false
      updateProductTourStatus({ [key]: false })
    },
    [updateProductTourStatus]
  )

  return {
    isSingleSlide: slides.length === 1,
    markSlideAsSeen,
    setSlideIndex,
    shouldShowInvoicingWelcomeModal: Boolean(slides.length),
    slideIndex,
    slides,
  }
}
